import {
    PlaneGeometry,
    MeshBasicMaterial,
    InstancedMesh,
    Object3D,
    Color,
    Vector3,
} from 'three'
import Experience from '../Experience.js'

function rand(seed, i) {
    const x = Math.sin(seed + i * 374761393) * 1274126177
    return x - Math.floor(x)
}

export default class Trees {
    constructor(terrain) {
        this.experience = new Experience()
        this.scene = this.experience.scene
        this.resources = this.experience.resources
        this.config = this.experience.config
        this.camera = this.experience.camera
        this.debug = this.experience.debug

        this.terrain = terrain

        this.treeMeshes = []
        this.gpuBillboard = true

        this._tmpColor = new Color()
        this._colorA = new Color()
        this._colorB = new Color()
        this._tmpDummy = new Object3D()
        this._tmpCameraDir = new Vector3()

        this.debugParams = {
            colorA: this.config.trees.colorA,
            colorB: this.config.trees.colorB,
            hue: this.config.trees.hue,
            saturation: this.config.trees.saturation,
            lightness: this.config.trees.lightness,
            opacity: this.config.trees.opacity ?? 1
        }

        // Partagés par les deux matériaux (A/B) : direction du soleil pour
        // l'ombrage contre-jour, temps pour le vent.
        this.shadingUniforms = {
            uTime: { value: 0 },
            uSunDir: { value: new Vector3(1, 0.6, 0.3).normalize() },
            uBacklight: { value: this.config.trees.backlight ?? 0.3 },
            uBaseOcclusion: { value: this.config.trees.baseOcclusion ?? 0.35 },
            uWind: { value: this.config.trees.wind ?? 0.02 }
        }

        this.treesData = []
        this.treesByType = {
            A: [],
            B: []
        }

        this.billboardConfig = {
            dynamicRadius: this.config.trees.dynamicBillboardRadius ?? 3.5,
            spatialCellSize: this.config.trees.spatialCellSize ?? 0.5,
            minCameraMove: this.config.trees.minCameraMove ?? 0.08,
            minCameraYawDelta: this.config.trees.minCameraYawDelta ?? 0.025,
            updateIntervalMs: this.config.trees.updateIntervalMs ?? 66
        }
        this.dynamicRadiusSq = this.billboardConfig.dynamicRadius * this.billboardConfig.dynamicRadius
        this.cellSize = this.billboardConfig.spatialCellSize
        this.spawnRadiusNormalized = this.config.trees.spawnRadiusNormalized ?? 0.92
        this.minDensityThreshold = this.config.trees.minDensityThreshold ?? 0.06
        this.qualityConfig = this.config.quality
        this.qualityLevel = this.resolveInitialQualityLevel()
        this.qualityProfile = this.qualityConfig?.presets?.[this.qualityLevel] ?? {}
        this.lastBillboardUpdate = 0
        this.lastCameraPosition = new Vector3()
        this.lastCameraYaw = 0
        this.firstBillboardUpdate = true
        this.spatialMapByType = { A: new Map(), B: new Map() }

        if (this.debug.active) {
            this.debugFolder = this.debug.ui.addFolder({ title: 'Trees', expanded: false })
            this.setDebug()
        }

        this.createTrees(
            this.resources.items.treeA,
            this.resources.items.treeB,
            this.config.trees.count
        )
        // this.downloadTreesData()
    }

    setDebug() {
        const colorsFolder = this.debugFolder.addFolder({ title: 'Couleurs', expanded: true })

        colorsFolder.addBinding(this.debugParams, 'colorA', { view: 'color', label: 'Couleur A' })
            .on('change', () => this.applyTreeColors())

        colorsFolder.addBinding(this.debugParams, 'colorB', { view: 'color', label: 'Couleur B' })
            .on('change', () => this.applyTreeColors())

        colorsFolder.addBinding(this.debugParams, 'hue', {
            label: 'Variation teinte',
            min: 0,
            max: 0.2,
            step: 0.001
        }).on('change', () => this.applyTreeColors())

        colorsFolder.addBinding(this.debugParams, 'saturation', {
            label: 'Variation saturation',
            min: 0,
            max: 1,
            step: 0.01
        }).on('change', () => this.applyTreeColors())

        colorsFolder.addBinding(this.debugParams, 'lightness', {
            label: 'Variation luminosité',
            min: 0,
            max: 1,
            step: 0.01
        }).on('change', () => this.applyTreeColors())

        colorsFolder.addBinding(this.debugParams, 'opacity', {
            label: 'Opacité',
            min: 0,
            max: 1,
            step: 0.01
        }).on('change', () => {
            this.treeMeshes.forEach(mesh => {
                mesh.material.opacity = this.debugParams.opacity
            })
        })
    }

    computeTreeColor(colorT, target = this._tmpColor) {
        this._colorA.set(this.debugParams.colorA)
        this._colorB.set(this.debugParams.colorB)
        target.lerpColors(this._colorA, this._colorB, colorT)

        const hsl = { h: 0, s: 0, l: 0 }
        target.getHSL(hsl)

        const variation = (colorT - 0.5) * 2
        hsl.h = (hsl.h + variation * this.debugParams.hue + 1) % 1
        hsl.s = Math.min(1, Math.max(0, hsl.s + variation * this.debugParams.saturation))
        hsl.l = Math.min(1, Math.max(0, hsl.l + variation * this.debugParams.lightness))

        target.setHSL(hsl.h, hsl.s, hsl.l)
        return target
    }

    applyTreeColors() {
        if (!this.treeMeshes.length) return

        const meshByType = {
            A: this.treeMeshes[0],
            B: this.treeMeshes[1]
        }

        for (let i = 0; i < this.treesData.length; i++) {
            const tree = this.treesData[i]
            const mesh = meshByType[tree.type]
            if (!mesh) continue

            this.computeTreeColor(tree.colorT)
            mesh.setColorAt(tree.instanceIndex, this._tmpColor)
        }

        meshByType.A.instanceColor.needsUpdate = true
        meshByType.B.instanceColor.needsUpdate = true
    }

    createTrees(treeTextureA, treeTextureB, maxCount = 1000) {

        const geometry = new PlaneGeometry(1, 1)
        geometry.translate(0, 0.5, 0)

        const materialA = this.createTreeMaterial(treeTextureA)
        const materialB = this.createTreeMaterial(treeTextureB)

        const meshA = new InstancedMesh(geometry, materialA, maxCount)
        const meshB = new InstancedMesh(geometry, materialB, maxCount)

        const width = this.terrain.geometry.parameters.width
        const height = this.terrain.geometry.parameters.height

        const seed = 1337

        const treeColors = this.config.trees

        let indexA = 0
        let indexB = 0
        const qualityMultiplier = this.qualityProfile?.treesCountMultiplier ?? 1
        const effectiveMaxCount = Math.max(500, Math.floor(maxCount * qualityMultiplier))

        this.treesData = []
        this.treesByType = { A: [], B: [] }
        this.spatialMapByType = { A: new Map(), B: new Map() }

        const spawnRadius = Math.min(width, height) * 0.5 * this.spawnRadiusNormalized
        const spawnRadiusSq = spawnRadius * spawnRadius

        for (let i = 0; i < effectiveMaxCount * 2; i++) {

            const r1 = rand(seed, i * 2)
            const r2 = rand(seed, i * 2 + 1)
            const r3 = rand(seed, i * 3)
            const r4 = rand(seed, i * 4)

            const x = (r1 - 0.5) * width
            const z = (r2 - 0.5) * height
            if ((x * x + z * z) > spawnRadiusSq) continue

            const density = this.terrain.getTreeDensityAt(x, z)
            if (density < this.minDensityThreshold) continue
            if (r3 > density) continue

            const y = this.terrain.getHeightAt(x, z)

            const scale = treeColors.scale * (1 + r4 * 2)
            const rotationY = rand(seed, i * 5) * Math.PI * 2
            const colorT = rand(seed, i * 7)

            const isA = rand(seed, i * 6) > 0.5

            this._tmpDummy.position.set(x, y, z)
            this._tmpDummy.rotation.y = rotationY
            this._tmpDummy.scale.set(scale * 8, scale * 12, 1)
            this._tmpDummy.updateMatrix()

            this.computeTreeColor(colorT)

            const data = {
                x,
                y,
                z,
                scale,
                rotation: rotationY,
                colorT,
                type: isA ? 'A' : 'B',
                instanceIndex: isA ? indexA : indexB
            }

            this.treesData.push(data)
            this.treesByType[data.type].push(data)
            this.addToSpatialMap(data)

            if (isA) {
                meshA.setMatrixAt(indexA, this._tmpDummy.matrix)
                meshA.setColorAt(indexA, this._tmpColor)
                indexA++
            } else {
                meshB.setMatrixAt(indexB, this._tmpDummy.matrix)
                meshB.setColorAt(indexB, this._tmpColor)
                indexB++
            }
        }

        meshA.count = indexA
        meshB.count = indexB

        meshA.instanceMatrix.needsUpdate = true
        meshB.instanceMatrix.needsUpdate = true

        meshA.instanceColor.needsUpdate = true
        meshB.instanceColor.needsUpdate = true

        meshA.renderOrder = 3
        meshB.renderOrder = 2

        this.scene.add(meshA, meshB)
        this.treeMeshes = [meshA, meshB]
    }

    // Sprites opaques à alpha-to-coverage (bords doux grâce au MSAA du
    // composer, plus de transparence « fantôme » ni de tri), avec un
    // ombrage bon marché qui leur donne du volume :
    // - contre-jour quand on regarde vers le soleil, plein soleil sinon ;
    // - pied du houppier plus sombre (auto-ombrage) ;
    // - léger balancement au vent, nul au pied, maximal en cime.
    createTreeMaterial(texture) {
        const opaque = this.debugParams.opacity >= 1
        const material = new MeshBasicMaterial({
            map: texture,
            transparent: !opaque,
            alphaTest: opaque ? 0.5 : 0.4,
            alphaToCoverage: opaque,
            depthWrite: opaque,
            opacity: this.debugParams.opacity
        })

        material.onBeforeCompile = (shader) => {
            Object.assign(shader.uniforms, this.shadingUniforms)

            shader.vertexShader = shader.vertexShader
                .replace(
                    '#include <common>',
                    `#include <common>
                    uniform float uTime;
                    uniform vec3 uSunDir;
                    uniform float uBacklight;
                    uniform float uWind;
                    varying float vTreeShade;
                    varying float vTreeHeight;`
                )
                .replace(
                    '#include <begin_vertex>',
                    `#include <begin_vertex>
                    vec3 treeOrigin = instanceMatrix[3].xyz;
                    float treeSeed = dot(treeOrigin.xz, vec2(12.9898, 78.233));
                    float sway = sin(uTime * 1.1 + treeSeed) * 0.6 + sin(uTime * 2.3 + treeSeed * 1.7) * 0.4;
                    transformed.x += sway * uWind * uv.y * uv.y;

                    vec2 toTree = treeOrigin.xz - cameraPosition.xz;
                    float facingSun = dot(normalize(toTree + 1e-5), normalize(uSunDir.xz + 1e-5));
                    vTreeShade = 1.0 - uBacklight * (facingSun * 0.5 + 0.5) + uBacklight * 0.35;
                    vTreeHeight = uv.y;`
                )
                // Billboard cylindrique calculé sur GPU pour toutes les
                // instances : plus aucun sprite vu de biais hors du rayon
                // de mise à jour CPU.
                .replace(
                    '#include <project_vertex>',
                    `vec2 treeToCamera = normalize(cameraPosition.xz - treeOrigin.xz + 1e-5);
                    vec3 treeRight = vec3(treeToCamera.y, 0.0, -treeToCamera.x);
                    float treeScaleX = length(instanceMatrix[0].xyz);
                    float treeScaleY = length(instanceMatrix[1].xyz);
                    vec3 treeWorld = treeOrigin
                        + treeRight * transformed.x * treeScaleX
                        + vec3(0.0, transformed.y * treeScaleY, 0.0);
                    vec4 mvPosition = viewMatrix * modelMatrix * vec4(treeWorld, 1.0);
                    gl_Position = projectionMatrix * mvPosition;`
                )

            shader.fragmentShader = shader.fragmentShader
                .replace(
                    '#include <common>',
                    `#include <common>
                    uniform float uBaseOcclusion;
                    varying float vTreeShade;
                    varying float vTreeHeight;`
                )
                .replace(
                    '#include <color_fragment>',
                    `#include <color_fragment>
                    float baseOcclusion = mix(1.0 - uBaseOcclusion, 1.0, smoothstep(0.08, 0.62, vTreeHeight));
                    diffuseColor.rgb *= vTreeShade * baseOcclusion;`
                )
        }

        return material
    }

    resolveInitialQualityLevel() {
        const configuredPreset = this.qualityConfig?.preset ?? 'auto'
        if (configuredPreset !== 'auto') return configuredPreset
        return this.experience.isMobile ? 'medium' : 'high'
    }

    applyQualityLevel(level) {
        const profile = this.qualityConfig?.presets?.[level]
        if (!profile || level === this.qualityLevel) return

        this.qualityLevel = level
        this.qualityProfile = profile

        this.treeMeshes.forEach(mesh => {
            this.scene.remove(mesh)
            mesh.geometry?.dispose?.()
            mesh.material?.dispose?.()
        })
        this.treeMeshes = []
        this.treesData = []
        this.treesByType = { A: [], B: [] }
        this.spatialMapByType = { A: new Map(), B: new Map() }

        this.createTrees(
            this.resources.items.treeA,
            this.resources.items.treeB,
            this.config.trees.count
        )
    }

    getCellKey(x, z) {
        const cx = Math.floor(x / this.cellSize)
        const cz = Math.floor(z / this.cellSize)
        return `${cx},${cz}`
    }

    addToSpatialMap(treeData) {
        const cellKey = this.getCellKey(treeData.x, treeData.z)
        const map = this.spatialMapByType[treeData.type]
        if (!map.has(cellKey)) map.set(cellKey, [])
        map.get(cellKey).push(treeData)
    }

    getTreesNearCamera(type, camX, camZ, radius) {
        const map = this.spatialMapByType[type]
        if (!map) return []

        const minX = Math.floor((camX - radius) / this.cellSize)
        const maxX = Math.floor((camX + radius) / this.cellSize)
        const minZ = Math.floor((camZ - radius) / this.cellSize)
        const maxZ = Math.floor((camZ + radius) / this.cellSize)

        const trees = []
        for (let x = minX; x <= maxX; x++) {
            for (let z = minZ; z <= maxZ; z++) {
                const key = `${x},${z}`
                const cellTrees = map.get(key)
                if (cellTrees?.length) trees.push(...cellTrees)
            }
        }
        return trees
    }

    downloadTreesData(filename = 'trees-data.json') {
        const json = JSON.stringify(this.treesData, null, 2)

        const blob = new Blob([json], { type: 'application/json' })
        const url = URL.createObjectURL(blob)

        const a = document.createElement('a')
        a.href = url
        a.download = filename
        a.click()

        URL.revokeObjectURL(url)
    }

    updateTreesBillboard(camera) {
        if (!this.treeMeshes.length) return
        // L'orientation est désormais faite dans le vertex shader.
        if (this.gpuBillboard) return

        camera.getWorldDirection(this._tmpCameraDir)

        const camX = camera.position.x
        const camZ = camera.position.z

        const cameraYaw = Math.atan2(this._tmpCameraDir.x, this._tmpCameraDir.z)
        const movedDistance = camera.position.distanceTo(this.lastCameraPosition)
        const yawDelta = Math.abs(cameraYaw - this.lastCameraYaw)
        const now = performance.now()

        const canUpdateByTime = now - this.lastBillboardUpdate >= this.billboardConfig.updateIntervalMs
        const shouldUpdate =
            this.firstBillboardUpdate ||
            (
                canUpdateByTime &&
                (
                    movedDistance >= this.billboardConfig.minCameraMove ||
                    yawDelta >= this.billboardConfig.minCameraYawDelta
                )
            )

        if (!shouldUpdate) return

        this._updateMesh(this.treeMeshes[0], 'A', camX, camZ)
        this._updateMesh(this.treeMeshes[1], 'B', camX, camZ)

        this.lastBillboardUpdate = now
        this.lastCameraPosition.copy(camera.position)
        this.lastCameraYaw = cameraYaw
        this.firstBillboardUpdate = false
    }

    _updateMesh(mesh, type, camX, camZ) {
        const trees = this.getTreesNearCamera(type, camX, camZ, this.billboardConfig.dynamicRadius)
        if (!trees?.length) return

        for (let i = 0; i < trees.length; i++) {
            const t = trees[i]

            const dx = camX - t.x
            const dz = camZ - t.z
            const distanceSq = dx * dx + dz * dz

            if (distanceSq > this.dynamicRadiusSq) {
                continue
            }

            const rotationY = Math.atan2(dx, dz)

            this._tmpDummy.position.set(t.x, t.y, t.z)
            this._tmpDummy.rotation.y = rotationY
            this._tmpDummy.scale.set(t.scale * 8, t.scale * 12, 1)
            this._tmpDummy.updateMatrix()

            mesh.setMatrixAt(t.instanceIndex, this._tmpDummy.matrix)
        }

        mesh.instanceMatrix.needsUpdate = true
    }

    update() {
        this.shadingUniforms.uTime.value = this.experience.time.elapsed * 0.001

        const sun = this.experience.world?.environment?.dirLight
        if (sun) this.shadingUniforms.uSunDir.value.copy(sun.position).normalize()

        this.updateTreesBillboard(this.camera.instance)
    }

    destroy() {
        if (this.debug?.active && this.debugFolder) {
            this.debugFolder.dispose()
            this.debugFolder = null
        }

        this.treeMeshes.forEach(mesh => {
            this.scene.remove(mesh)
            mesh.geometry?.dispose?.()
            mesh.material?.dispose?.()
        })
        this.treeMeshes = []
        this.treesData = []
        this.treesByType = { A: [], B: [] }
    }
}